import { SESSION_KEY } from '../constants.js';
import { timeoutScheduler } from '../utils/scheduler.js';
import { timestampGenerator } from '../utils/timestamps.js';
const defaultConfig = {
    logger: {
        trace: () => { },
        info: () => { },
        debug: () => { },
        warn: () => { },
        error: () => { },
        fatal: () => { },
    },
    scheduler: timeoutScheduler(),
    sessionResolution: 10 * 1000,
    timestampGenerator: timestampGenerator,
    onNewSession: () => { },
    sessionIdGenerator: () => '123s',
};
/**
 * Session manager is responsible to create and periodically update session metadata.
 */
export const sessionManager = async (inputConfig) => {
    const config = {
        ...defaultConfig,
        ...inputConfig,
    };
    let isNewSession = false;
    const createNewSession = () => {
        config.logger.trace('Starting new session.');
        isNewSession = true;
        const session = {
            id: config.uuidFactory(),
            start: config.timestampGenerator(),
            updatedAt: config.timestampGenerator(),
        };
        config.onNewSession(session.id, session.start);
        return session;
    };
    // The session is valid if the last update is newer than the current timestamp minus session resolution.
    const isSessionValid = (session) => {
        return (session && session.updatedAt + config.sessionResolution / 1000 > config.timestampGenerator()) ?? false;
    };
    // Update session metadata. It creates a new one if the found is expired.
    const updateSession = async () => {
        const savedSession = await config.persistenceManager.getValue(SESSION_KEY);
        const updatedSession = isSessionValid(savedSession) ? savedSession : createNewSession();
        await config.persistenceManager.saveValue(SESSION_KEY, {
            id: updatedSession.id,
            start: updatedSession.start,
            updatedAt: config.timestampGenerator(),
        });
        config.scheduler.scheduleAction(updateSession, config.sessionResolution / 2, 'updateSession');
    };
    // Get a valid session. If not found, it waits and try again later.
    const getSession = () => {
        return new Promise((resolve, reject) => {
            let retries = 0;
            const waitForSessionToBeReady = async () => {
                const savedSession = await config.persistenceManager.getValue(SESSION_KEY);
                if (isSessionValid(savedSession))
                    return resolve(savedSession);
                if (++retries > 10)
                    reject(new Error('Could not get a valid session!'));
                config.scheduler.scheduleAction(waitForSessionToBeReady, 100, 'readSession');
            };
            waitForSessionToBeReady();
        });
    };
    // Once the session manager is created, the process to update session metadata is started.
    // Only once instance at a time can update the session metadata.
    config.runInLock('picoUpdateSession', updateSession);
    // Before returning the session manager, we wait for the session to be ready.
    const currentSession = await getSession();
    return {
        isEnabled: () => true,
        isNewSession: () => isNewSession,
        getSessionStartId: () => currentSession.id,
        getSessionStart: () => currentSession.start,
        getSessionId: config.sessionIdGenerator,
    };
};
//# sourceMappingURL=sessionManagerImpl.js.map